import React, { useRef } from 'react';
import Slider from 'react-slick';
import ConditionalLink from '@plone/volto/components/manage/ConditionalLink/ConditionalLink';
import { flattenToAppURL, isInternalURL } from '@plone/volto/helpers/Url/Url';
import cx from 'classnames';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

interface CarrouselTemplateProps {
  items: any[];
  linkTitle?: string;
  linkHref?: any[];
  isEditMode?: boolean;
  autoplay?: boolean;
  autoplaySpeed?: number;
  speed?: number;
  infinite?: boolean;
  pauseOnHover?: boolean;
  showDots?: boolean;
}

const CarrouselTemplate: React.FC<CarrouselTemplateProps> = (props) => {
  const {
    items,
    linkTitle,
    linkHref,
    isEditMode,
    autoplay = true,
    autoplaySpeed = 4000,
    speed = 1000,
    infinite = true,
    pauseOnHover = true,
    showDots = true,
  } = props;

  const sliderRef = useRef<Slider>(null);

  let link = null;
  let href = linkHref?.[0]?.['@id'] || '';
  if (isInternalURL(href)) {
    link = (
      <ConditionalLink to={flattenToAppURL(href)} condition={!isEditMode}>
        {linkTitle || href}
      </ConditionalLink>
    );
  } else if (href) {
    link = <a href={href}>{linkTitle || href}</a>;
  }

  const getImageUrl = (item: any): string => {
    try {
      if (item.image_scales?.preview_image_link?.[0]) {
        const imageData = item.image_scales.preview_image_link[0];
        if (imageData.scales) {
          const scale =
            imageData.scales.larger ||
            imageData.scales.large ||
            imageData.scales.preview ||
            imageData.scales.medium;

          if (scale && scale.download) {
            return `${imageData.base_path}/${scale.download}`;
          }
        }

        if (imageData.download) {
          return `${imageData.base_path}/${imageData.download}`;
        }
      }
    } catch (error) {}

    return 'https://www.ifpb.edu.br/comunicacao/publicacoes/marca-ifpb/marca-em-jpg/geral.jpg';
  };

  const formatDate = (dateString: string): string => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return '';
    }
  };

  const sliderSettings = {
    ref: sliderRef,
    dots: showDots,
    infinite: infinite,
    speed: speed,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: autoplay,
    autoplaySpeed: autoplaySpeed,
    draggable: true,
    swipeToSlide: true,
    touchThreshold: 10,
    pauseOnHover: pauseOnHover,
    pauseOnFocus: true,
    fade: false,
    cssEase: 'ease-in-out',
    centerMode: false,
    centerPadding: '0px',
    lazyLoad: undefined,
    waitForAnimate: false,
    useCSS: true,
    useTransform: true,
    variableWidth: false,
    adaptiveHeight: false,
    accessibility: true,
    focusOnSelect: false,
    initialSlide: 0,
    arrows: false,
    responsive: [
      {
        breakpoint: 768,
        settings: {
          speed: Math.max(speed - 200, 300),
          autoplaySpeed: Math.max(autoplaySpeed - 500, 1000),
        },
      },
    ],
  };

  return (
    <div className="carousel-container">
      <div className="items">
        <div className="listing carousel">
          <Slider {...sliderSettings}>
            {items.map((item, index) => {
              const imageUrl = getImageUrl(item);

              return (
                <div
                  className={cx('listing-item', 'carousel-slide')}
                  key={item['@id']}
                >
                  <div className="listing-body">
                    <div className="listing-carousel-item">
                      <ConditionalLink
                        to={flattenToAppURL(item['@id'])}
                        condition={!isEditMode}
                      >
                        <div className="highlight-image-wrapper">
                          <img
                            src={imageUrl}
                            alt={item.title || ''}
                            loading="eager"
                            className="carousel-image"
                          />
                          <div className="listing-carousel-pickgradient">
                            <div className="listing-carousel-item-title">
                              <div className="listing-carousel-title">
                                <h3>{item?.title}</h3>
                                {(item?.created || item?.Date) && (
                                  <span className="listing-carousel-item-created">
                                    {formatDate(item?.created || item?.Date)}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </ConditionalLink>
                    </div>
                  </div>
                </div>
              );
            })}
          </Slider>
        </div>
      </div>

      {link && <div className="footer">{link}</div>}
    </div>
  );
};

export default CarrouselTemplate;
